import type { ReactElement } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";

type CreateRootOptions = NonNullable<Parameters<typeof createRoot>[1]>;
export type RootErrorHandler = NonNullable<CreateRootOptions["onUncaughtError"]>;
type RootErrorOptions = Pick<CreateRootOptions, "onRecoverableError" | "onUncaughtError">;

interface HydrationMarker {
  dataset: DOMStringMap;
}

interface MountInertiaAppOptions {
  app: ReactElement;
  el: HTMLElement;
  onReactError: RootErrorHandler | null;
}

const createRootErrorOptions = (
  onReactError: RootErrorHandler | null,
): RootErrorOptions | undefined =>
  onReactError === null
    ? undefined
    : {
        onRecoverableError: onReactError,
        onUncaughtError: onReactError,
      };

const shouldHydrateInertiaApp = (el: Readonly<HydrationMarker>) =>
  Object.hasOwn(el.dataset, "serverRendered");

export const mountInertiaApp = ({ app, el, onReactError }: Readonly<MountInertiaAppOptions>) => {
  const rootOptions = createRootErrorOptions(onReactError);

  if (shouldHydrateInertiaApp(el)) {
    hydrateRoot(el, app, rootOptions);

    return;
  }

  const root = rootOptions === undefined ? createRoot(el) : createRoot(el, rootOptions);

  root.render(app);
};
